'use client';

import clsx from 'clsx';
import { motion } from 'framer-motion';
import { CircleX } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useFreezeScroll } from '@/hooks/use-freeze-scroll';
import { useKeydown } from '@/hooks/use-keydown';

import { confirmWithDialog, ConfirmWithDialogArgs } from './confirm-dialog';
import { Portal } from './portal';
import { Button } from './ui/button';

const DRAWER_VIEWPORT_ID = 'drawer-viewport';

export type DrawerProps = {
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  confirmClose?: ConfirmWithDialogArgs;
  defaultOpen?: boolean;
  disabledFooterToggle?: boolean;
  drawerRef?: React.RefObject<HTMLDivElement>;
  onClose?: () => void;
  onConfirmCloseCancel?: () => void;
  onOpen?: () => void;
  scrim?: boolean;
  title: string;
  trigger: React.ReactNode;
  triggerClassName?: string;
};

export function Drawer({
  actions,
  children,
  className,
  confirmClose,
  defaultOpen = false,
  disabledFooterToggle = false,
  drawerRef,
  onClose,
  onConfirmCloseCancel,
  onOpen,
  scrim = false,
  title,
  trigger,
  triggerClassName,
}: DrawerProps) {
  const initialized = useRef(false);
  const wrapperId = useId();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const isOpenDebounced = useDebouncedValue(isOpen, { millis: 1000 });
  const isRendered = isOpen || isOpenDebounced;
  const motionState = isOpen ? 'open' : 'closed';
  const showScrim = useDebouncedValue(scrim && isOpen, { millis: 50 });
  const toggle = useCallback(() => {
    setIsOpen(!isOpen);

    if (isOpen && onClose) {
      onClose();
    }
  }, [isOpen, onClose]);
  const onKeydown = useCallback(
    (e: Event) => {
      const event = e as KeyboardEvent;

      event.key === 'Escape' && toggle();
    },
    [toggle]
  );

  useFreezeScroll(isOpen);

  useKeydown({ isActive: isOpen, callback: onKeydown });

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      defaultOpen && onOpen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div
        className={triggerClassName}
        onClick={() => {
          onOpen?.();
          toggle();
        }}
      >
        {trigger}
      </div>

      <Portal id={DRAWER_VIEWPORT_ID}>
        {isRendered && (
          <section
            className={clsx(
              `drawer-is-${motionState}`,
              `drawer-scrim-${showScrim ? 'show' : 'hide'}`,
              'fixed top-0 bottom-0 right-0 z-10 max-w-full'
            )}
          >
            <motion.div
              animate={motionState}
              className='drawer-content bg-primary text-primary-foreground sm:rounded-l-xl border-2 sm:w-[calc(75vw)] md:w-[30rem] w-[100vw] h-full'
              initial={{ x: 'calc(100%)' }}
              variants={{ open: { x: 0 }, closed: { x: 'calc(100%)' } }}
            >
              <div
                className={clsx('wrapper', 'flex flex-col sm:h-[calc(100vh)] h-full p-4', className)}
                id={wrapperId}
                ref={drawerRef}
              >
                <Button
                  autoFocus
                  className='drawer-close-button absolute top-3 right-3'
                  disabled={!isOpen}
                  name='close'
                  onClick={async () => {
                    const confirmed = confirmClose ? await confirmWithDialog(confirmClose) : true;

                    if (confirmed) {
                      toggle();
                    } else if (onConfirmCloseCancel) {
                      onConfirmCloseCancel();
                    }
                  }}
                  variant='ghost'
                >
                  <CircleX />
                </Button>

                <div className='p-4'>
                  <h3 className='text-xl font-bold'>{title}</h3>
                </div>
                <div className='content hidden-scroll flex-1 p-4'>{children}</div>
                <div
                  className='actions toggle flex flex-row-reverse gap-4'
                  onClick={() => !disabledFooterToggle && toggle()}
                >
                  {actions}
                </div>
              </div>
            </motion.div>
          </section>
        )}
      </Portal>
    </>
  );
}

export function closeHighestDrawer() {
  const buttons = document.getElementById(DRAWER_VIEWPORT_ID)?.querySelectorAll('.drawer-close-button');
  const lastButton = buttons && buttons[buttons.length - 1];

  if (lastButton) {
    (lastButton as HTMLElement).click();
  }
}

export function DrawerViewport() {
  return (
    <div id={DRAWER_VIEWPORT_ID}>
      <div className='drawer-viewport-overlay fixed inset-0 z-10' onClick={closeHighestDrawer} />
    </div>
  );
}
